/* eslint-disable react/display-name */
import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import { generateSelectorsFromTestIds } from 'helpers/utilities';

import Button, { ButtonGroup } from 'components/Button';
import Flex from 'components/Flex';
import TextHeader from 'components/TextHeader';
import Tooltip from 'components/Tooltip';

import Wizard from './Wizard';
import WizardBreadCrumb from './WizardBreadcrumb';
import WizardBackButton from './WizardBackHeader';

/*
 * [WizardStep] - Layout component for rendering content for a given wizard step
 *
 * @param {string} [title]
 * @param {string} [subtitle]
 * @param {node} children
 *
 * Props from Wizard component
 * @param {object[]} steps - steps for the wizard, used to determine if wizard actions should be rendered
 * @param {number} stepIndex - current step in the wizard flow. Pass null if displaying the overview page
 * @param {func} [onContinue] - if passed, function is run when there is another step
 */

const WizardStep = ({
  title,
  subtitle,
  onContinue,
  isContinueDisabled = false,
  continueButtonText,
  continueDisabledText,
  onPrevious,
  children,
  // wizard props
  // TODO: consider moving wizard props into context
  steps,
  stepIndex,
  backText,
  backPath,
  context,
  overviewTitle,
  requiredStepIndex
}) => {
  return (
    <Wizard>
      <Wizard.Header>
        {steps?.length > 1 && (
          <Flex grow={0} align="center">
            <WizardBreadCrumb
              steps={steps}
              stepIndex={stepIndex}
              title={overviewTitle}
              overviewPath={`/artist-profile-editor/${context}/overview`}
              requiredStepIndex={requiredStepIndex}
            />
          </Flex>
        )}

        {steps?.length === 1 && (
          <WizardBackButton
            backText={backText}
            backPath={backPath}
            data-testid={WizardStep.TEST_IDS.BACK_BUTTON}
          />
        )}
      </Wizard.Header>

      <Wizard.Content>
        <div>
          {title && <WizardStep.Title>{title}</WizardStep.Title>}
          {subtitle && <WizardStep.Subtitle>{subtitle}</WizardStep.Subtitle>}
        </div>

        <div>{children}</div>

        <WizardStepActions
          onContinue={onContinue}
          onPrevious={onPrevious}
          isContinueDisabled={isContinueDisabled}
          continueDisabledText={continueDisabledText}
          continueButtonText={continueButtonText}
        />
      </Wizard.Content>
    </Wizard>
  );
};

WizardStep.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  children: PropTypes.node,
  // wizard props: TODO: move to common proptypes
  backText: PropTypes.string,
  backPath: PropTypes.string,
  overviewTitle: PropTypes.string,
  context: PropTypes.string,
  requiredStepIndex: PropTypes.number,
  steps: PropTypes.arrayOf(PropTypes.shape()),
  isContinueDisabled: PropTypes.bool,
  continueButtonText: PropTypes.string,
  continueDisabledText: PropTypes.string,
  stepIndex: PropTypes.number,
  onContinue: PropTypes.func,
  onPrevious: PropTypes.func
};

WizardStep.Title = ({ children }) => (
  <TextHeader
    type={TextHeader.TYPES.LARGE}
    style={{ marginBottom: '0.3rem' }}
    weight={TextHeader.WEIGHTS.HEAVY}
    data-testid={WizardStep.TEST_IDS.TITLE}
  >
    {children}
  </TextHeader>
);

WizardStep.Subtitle = ({ children }) => (
  <p data-testid={WizardStep.TEST_IDS.SUBTITLE}>{children}</p>
);

const SectionHeader = styled.div`
  ${({ title, subtitle }) =>
    (title || subtitle) &&
    `
    margin-bottom: 1rem;
  `}
`;

WizardStep.Section = ({ children, title, subtitle }) => (
  <div className="pt-2 pb-1">
    <SectionHeader title={title} subtitle={subtitle}>
      {title && (
        <TextHeader type={TextHeader.TYPES.MEDIUM} style={{ marginBottom: '0.3rem' }}>
          {title}
        </TextHeader>
      )}
      {subtitle && <div>{subtitle}</div>}
    </SectionHeader>

    {children}
  </div>
);

const WizardStepActions = ({
  nextPath,
  prevPath,
  onContinue,
  onPrevious,
  isContinueDisabled,
  continueButtonText = 'Continue',
  previousButtonText = 'Go back',
  continueDisabledText = 'This step must be completed before proceeding'
}) => (
  <ButtonGroup>
    {(nextPath || onContinue) && (
      <Tooltip
        enabled={isContinueDisabled}
        content={continueDisabledText}
        placement="bottom"
      >
        <Button
          data-testid={WizardStep.TEST_IDS.NEXT_STEP_BUTTON}
          disabled={isContinueDisabled}
          onClick={onContinue}
        >
          {continueButtonText}
        </Button>
      </Tooltip>
    )}

    {(prevPath || onPrevious) && (
      <Button
        data-testid={WizardStep.TEST_IDS.PREVIOUS_STEP_BUTTON}
        color={Button.COLORS.TRANSPARENT}
        onClick={onPrevious}
      >
        {previousButtonText}
      </Button>
    )}
  </ButtonGroup>
);

WizardStep.TEST_IDS = {
  TITLE: 'WizardStep-title',
  SUBTITLE: 'WizardStep-subtitle',
  PREVIOUS_STEP_BUTTON: 'WizardStep-previous-step',
  NEXT_STEP_BUTTON: 'WizardStep-next-step',
  BACK_BUTTON: 'WizardStep-back-btn'
};

WizardStep.SELECTORS = generateSelectorsFromTestIds(WizardStep.TEST_IDS);

WizardStepActions.propTypes = {
  continueButtonText: PropTypes.string,
  previousButtonText: PropTypes.string,
  nextPath: PropTypes.string,
  prevPath: PropTypes.string,
  onContinue: PropTypes.func,
  onPrevious: PropTypes.func,
  isContinueDisabled: PropTypes.bool,
  continueDisabledText: PropTypes.string
};

export default WizardStep;
